import { useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Badge } from 'react-bootstrap';
import config from '../config';
import { loggedInUserId } from '../utils/loggedInUserDetails';

export const useDashboardActions = (fetchDashboardData) => {
  // Modal states
  const [showBOMModal, setShowBOMModal] = useState(false);
  const [showProductionBatchModal, setShowProductionBatchModal] = useState(false);

  // Selected data states
  const [selectedSO, setSelectedSO] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [bomList, setBomList] = useState([]);
  const [productionBatches, setProductionBatches] = useState([]);
  const [loadingBOM, setLoadingBOM] = useState(false);
  const [loadingBatches, setLoadingBatches] = useState(false);

  const getSOStatusBadge = (status) => {
    const statusMap = {
      'Created': { bg: 'secondary', text: 'New' },
      'Stock Pending': { bg: 'warning', text: 'Stock Pending' },
      'Awaiting Receipt of PO (RM)': { bg: 'warning', text: 'Awaiting PO (RM)' },
      'Awaiting Receipt of PO (FG)': { bg: 'warning', text: 'Awaiting PO (FG)' },
      'Production Pending': { bg: 'warning', text: 'Production Pending' },
      'In Production': { bg: 'info', text: 'In Production' },
      'Production Complete': { bg: 'primary', text: 'Production Complete' },
      'Filled From Production & QC Pending': { bg: 'primary', text: 'Filled From Production & QC Pending' },
      'Filled From Production & Ready for Dispatch': { bg: 'primary', text: 'Filled From Production & Ready for Dispatch' },
      'Finalized': { bg: 'info', text: 'Ready for Dispatch' },
      'Ready for Dispatch': { bg: 'info', text: 'Ready for Dispatch' },
      'Loading': { bg: 'primary', text: 'Loading' },
      'Dispatch': { bg: 'success', text: 'Dispatched' },
      'Delivery': { bg: 'success', text: 'In Transit' },
      'Completed': { bg: 'dark', text: 'Delivered' },
      'On Hold': { bg: 'danger', text: 'On Hold' }
    };

    const statusInfo = statusMap[status] || { bg: 'secondary', text: status };
    return <Badge bg={statusInfo.bg} className="fs-11">{statusInfo.text}</Badge>;
  };

  const getPOStatusBadge = (status) => {
    const statusMap = {
      'Created': { bg: 'secondary', text: 'New Order' },
      'Partial Received': { bg: 'warning', text: 'Partially Received' },
      'Fully Received': { bg: 'success', text: 'Received' },
      'Closed': { bg: 'dark', text: 'Closed' },
      'Cancelled': { bg: 'danger', text: 'Cancelled' }
    };

    const statusInfo = statusMap[status] || { bg: 'secondary', text: status };
    return <Badge bg={statusInfo.bg}>{statusInfo.text}</Badge>;
  };

  const handleRaiseProductionRequest = useCallback(async (bom) => {
    if (!selectedSO || !selectedItem) {
      toast.error('Select a sales order and item first');
      return;
    }

    try {
      setLoadingBOM(true);

      const bomId = bom._id || bom;
      const productId = selectedItem.product?._id || selectedItem.product;

      const payload = {
        requestType: 'SALES_ORDER',
        requestedBy: loggedInUserId().userId,
        requiredDate: selectedSO.expectedDeliveryDate 
          ? new Date(new Date(selectedSO.expectedDeliveryDate).getTime() - 24 * 60 * 60 * 1000) 
          : new Date(Date.now() - 24 * 60 * 60 * 1000),
        salesOrders: [
          {
            salesOrderId: selectedSO._id || selectedSO,
            salesOrderItemId: selectedItem._id || selectedItem.salesOrderItemId || null,
            quantityFromOrder: selectedItem.quantity
          }
        ],
        salesOrderNumber: selectedSO.orderNumber,
        productId: productId,
        bomId: bomId,
        quantityRequired: selectedItem.quantity,
        unit: selectedItem.unit,
        priority: 'high',
        notes: `Auto-generated via BOM modal for SO ${selectedSO.orderNumber}`
      };

      if (bomId) {
        try {
          const pdResp = await axios.get(`${config.API_BASE_URL}/process-definitions/${bomId}`);
          const processDef = pdResp.data;

          if (processDef && Array.isArray(processDef.rawMaterials) && processDef.rawMaterials.length > 0) {
            const rawMaterialsForRequest = processDef.rawMaterials.map(rm => ({
              materialId: rm.materialId?._id || rm.materialId,
              requiredQuantity: (rm.quantity || 0) * (selectedItem.quantity || 1),
              unit: rm.unit || selectedItem.unit || '',
              materialName: rm.materialId?.productName || ''
            }));

            payload.rawMaterials = rawMaterialsForRequest;
          }
        } catch (pdErr) {
          console.warn('Failed to fetch process definition for bom while auto-creating PR', pdErr?.message || pdErr);
        }
      }

      const prResp = await axios.post(`${config.API_BASE_URL}/production-requests`, payload);
      const prId = prResp.data._id || prResp.data.productionRequest?._id || prResp.data.request?._id;
      if (!prId) throw new Error('Production request creation failed');

      toast.success('Production request created successfully');

      try {
        const so_id = selectedSO._id || selectedSO;
        await axios.patch(`${config.API_BASE_URL}/sales-orders/${so_id}/status`, { status: 'In Production Request' });
        toast.success('Sales order status updated to In Production Request');
      } catch (soErr) {
        console.warn('Failed to update sales order status after PR creation', soErr?.message || soErr);
      }

      setShowBOMModal(false);
      setSelectedItem(null);
      setSelectedSO(null);
      if (fetchDashboardData) await fetchDashboardData();

    } catch (err) {
      console.error('Error auto-creating production request:', err);
      toast.error(err.response?.data?.message || err.message || 'Failed to create production request');
    } finally {
      setLoadingBOM(false);
    }
  }, [selectedSO, selectedItem, fetchDashboardData]);

  const handleCheckBOM = useCallback(async (item, soId) => {
    setSelectedItem(item);
    setLoadingBOM(true);
    setShowBOMModal(true);
    setSelectedSO(soId);

    try {
      const [bomRes, batchesRes] = await Promise.all([
        axios.get(`${config.API_BASE_URL}/process-definitions/outputs?productId=${item.product._id}`),
        axios.get(`${config.API_BASE_URL}/production-batches/available/${item.product._id}`)
      ]);

      setBomList(bomRes.data);
      setProductionBatches(batchesRes.data);
    } catch (error) {
      console.error('Error fetching BOM/Batches:', error);
      toast.error('Error loading BOM data');
    } finally {
      setLoadingBOM(false);
    }
  }, []);

  const handleRunningProduction = useCallback(async (item, soId) => {
    setSelectedItem(item);
    setSelectedSO(soId);
    setLoadingBatches(true);
    setShowProductionBatchModal(true);

    try {
      const response = await axios.get(`${config.API_BASE_URL}/production-batches/available/${item.product._id}`);
      setProductionBatches(response.data);
    } catch (error) {
      console.error('Error fetching production batches:', error);
      toast.error('Error loading production batches');
    } finally {
      setLoadingBatches(false);
    }
  }, []);

  const handleCreatePurchaseRequest = useCallback(async (bomItem, requiredQuantity, soId) => {
    try {
      await axios.post(`${config.API_BASE_URL}/purchase-requests`, {
        items: [{
          product: bomItem.materialId?._id || bomItem.product?._id || bomItem._id,
          approx_quantity: requiredQuantity,
          quantity_unit: bomItem.unit
        }],
        priority: 'high',
        requested_by: loggedInUserId().userId,
        notes: `Auto-generated for SO requirement`,
        soId: soId?._id || soId,
      });

      toast.success('Purchase request created successfully');
      setShowBOMModal(false);
      if (fetchDashboardData) await fetchDashboardData();
    } catch (error) {
      console.error('Error creating purchase request:', error);
      toast.error('Error creating purchase request');
    }
  }, [fetchDashboardData]);

  const handleCreateCommonPurchaseRequest = useCallback(async (missingItems, soId) => {
    try {
      const items = missingItems.map(item => ({
        product: item.rm.materialId?._id || item.rm.product?._id || item.rm._id,
        approx_quantity: item.shortfall,
        quantity_unit: item.rm.unit
      }));

      const so_id = soId?._id || soId;

      await axios.post(`${config.API_BASE_URL}/purchase-requests`, {
        items,
        priority: 'high',
        requested_by: loggedInUserId().userId,
        notes: `Common PR for missing raw materials - SO requirement`,
        soId: so_id,
      });

      await axios.patch(`${config.API_BASE_URL}/sales-orders/${so_id}/status`, {
        status: 'Awaiting Receipt of PO (RM)'
      });

      toast.success('Common purchase request created and SO status updated');
      setShowBOMModal(false);
      if (fetchDashboardData) await fetchDashboardData();
    } catch (error) {
      console.error('Error creating common purchase request:', error);
      toast.error('Error creating common purchase request');
    }
  }, [fetchDashboardData]);

  const handleCreateFGPR = useCallback(async (item, soId) => {
    try {
      const so_id = soId?._id || soId;
      await axios.post(`${config.API_BASE_URL}/purchase-requests`, {
        items: [{
          product: item.product?._id,
          approx_quantity: item.quantity,
          quantity_unit: item.unit
        }],
        priority: 'high',
        requested_by: loggedInUserId().userId,
        notes: `FG Purchase Request for SO requirement`,
        soId: so_id,
      });

      await axios.patch(`${config.API_BASE_URL}/sales-orders/${so_id}/status`, {
        status: 'Awaiting Receipt of PO (FG)'
      });

      toast.success('Finished Good PR created and SO status updated');
      setShowBOMModal(false);
      if (fetchDashboardData) await fetchDashboardData();
    } catch (error) {
      console.error('Error creating FG PR:', error);
      toast.error('Error creating FG PR');
    }
  }, [fetchDashboardData]);

  const handleAssignToBatch = useCallback(async (batchId) => {
    if (!selectedSO || !selectedItem) {
      toast.error('Missing sales order or item information');
      return;
    }

    try {
      setLoadingBatches(true);

      const batchResp = await axios.get(`${config.API_BASE_URL}/production-batches/${batchId}`);
      const batch = batchResp.data;

      const productId = selectedItem?.product?._id || (batch?.outputs && (batch.outputs[0]?.productId?._id || batch?.outputs[0]?.productId));
      const bomId = batch?.bomId?._id || batch?.bomId;

      const productionRequestPayload = {
        requestType: 'SALES_ORDER',
        requestedBy: loggedInUserId().userId,
        requiredDate: selectedSO?.expectedDeliveryDate 
          ? new Date(new Date(selectedSO.expectedDeliveryDate).getTime() - 24 * 60 * 60 * 1000) 
          : new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
        salesOrders: [
          {
            salesOrderId: selectedSO._id || selectedSO,
            salesOrderItemId: selectedItem._id || selectedItem.salesOrderItemId || null,
            quantityFromOrder: selectedItem.quantity
          }
        ],
        salesOrderNumber: selectedSO.orderNumber,
        productId: productId,
        bomId: bomId,
        quantityRequired: selectedItem.quantity,
        unit: selectedItem.unit,
        priority: 'high',
        status: 'approved',
        notes: `Auto-assigned to batch ${batch.batchNumber}`
      };

      if (bomId) {
        try {
          const pdResp = await axios.get(`${config.API_BASE_URL}/process-definitions/${bomId}`);
          const processDef = pdResp.data;

          if (processDef && Array.isArray(processDef.rawMaterials) && processDef.rawMaterials.length > 0) {
            const rawMaterialsForRequest = processDef.rawMaterials.map(rm => ({
              materialId: rm.materialId?._id || rm.materialId,
              requiredQuantity: (rm.quantity || 0) * (selectedItem.quantity || 1),
              unit: rm.unit || selectedItem.unit || '',
              materialName: rm.materialId?.productName || ''
            }));

            productionRequestPayload.rawMaterials = rawMaterialsForRequest;
          }
        } catch (pdErr) {
          console.warn('Failed to fetch process definition for bom while auto-assigning', pdErr?.message || pdErr);
        }
      }

      const prResp = await axios.post(`${config.API_BASE_URL}/production-requests`, productionRequestPayload);
      const prId = prResp.data._id || prResp.data.productionRequest?._id || prResp.data.request?._id;

      await axios.post(`${config.API_BASE_URL}/production-batches/${batchId}/assign-request`, {
        productionRequestId: prId
      });

      const so_id = selectedSO._id || selectedSO;
      await axios.patch(`${config.API_BASE_URL}/sales-orders/${so_id}/status`, {
        status: 'In Production'
      });

      toast.success(`Assigned to batch ${batch.batchNumber} successfully`);
      setShowProductionBatchModal(false);
      setShowBOMModal(false);
      if (fetchDashboardData) await fetchDashboardData();
    } catch (error) {
      console.error('Error assigning to batch:', error);
      toast.error(error.response?.data?.message || 'Failed to assign to batch');
    } finally {
      setLoadingBatches(false);
    }
  }, [selectedSO, selectedItem, fetchDashboardData]);

  return {
    showBOMModal, setShowBOMModal,
    showProductionBatchModal, setShowProductionBatchModal,
    selectedSO, setSelectedSO,
    selectedItem, setSelectedItem,
    bomList, setBomList,
    productionBatches, setProductionBatches,
    loadingBOM, setLoadingBOM,
    loadingBatches, setLoadingBatches,
    getSOStatusBadge,
    getPOStatusBadge,
    handleRaiseProductionRequest,
    handleCheckBOM,
    handleRunningProduction,
    handleCreatePurchaseRequest,
    handleCreateCommonPurchaseRequest,
    handleCreateFGPR,
    handleAssignToBatch
  };
};
